import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Share2 } from "lucide-react";
import AppleBadge from "@/assets/appleBadge.svg";
import { Sidebar, StatCard, GridCard, ListCard, Button } from "@/components";
import { useClosetStore, useUserStore } from "@/store";
import { MEMBERS, MEMBER_NAMES, SAMPLE_ITEMS } from "@/data/sample";
import obStyles from "@/pages/Onboarding/Onboarding.module.css";
import styles from "./ClosetPage.module.css";
import EmptyGridCard from "@/components/Card/EmptyGridCard";
import EmptyListCard from "@/components/Card/EmptyListCard";
import { ClothingItem } from "@/types/product";
import Toolbar from "@/components/Toolbar/ToolBar";
import ShareModal from "@/components/Sidebar/ShareModal";
import { trackItemViewed, trackShareModalOpened, trackLogoutConfirmed } from "@/services/analytics";
import ProductDetailsModal from "@/components/ProductDetailsModal";
import ScrollIndicator from "./components/ScrollIndicator";

const PAGE_SIZE = 12;
const SCROLL_THRESHOLD = 200;

export function ClosetPage() {
  const navigate = useNavigate();
  const { syncStatus, syncProgress, logout, user, pollSyncProgress, completeOnboarding } = useUserStore();
  const {
    items,
    activeMemberId,
    activeCategoryName,
    viewMode,
    reset: resetCloset,
    getClosetItems,
    loadingInitialPage,
    loadingNextPage,
    filter
  } = useClosetStore();

  const [modalOpen, setModalOpen] = useState(false);
  const [sharingModalOpen, setSharingModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ClothingItem | null>(null);
  const [scrolled, setScrolled] = useState(false);

  const syncing = syncStatus === "syncing";

  const containerRef = useRef<HTMLDivElement>(null);
  const hasMore = filter.next_page !== null && filter.next_page !== undefined;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;

      if (scrollTop >= SCROLL_THRESHOLD) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      if (scrollHeight - scrollTop - clientHeight < 200 && hasMore && !loadingNextPage) {
        getClosetItems({ page: filter.next_page!, page_size: PAGE_SIZE }, true);
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingNextPage, filter.next_page]);

  useEffect(() => {
    getClosetItems({ page: 1, page_size: PAGE_SIZE });
    pollSyncProgress();

    // Mark onboarding as complete when user first reaches the closet
    if (!user?.completed_onboarding_at) {
      completeOnboarding();
    }
  }, []);

  // Stats
  const totalValue = user?.total_value ? Math.round(Number(user.total_value)) : "";
  const uniqueBrands = user?.brands_count || 0;
  const avaragePrice = user?.average_product_price ? Math.round(Number(user.average_product_price)) : "";
  const showMemberBadge = activeMemberId === "all";

  function handleItemClick(item: ClothingItem) {
    trackItemViewed({
      id: item.id,
      name: item.name,
      brand: item.brand?.name,
      category: item.category_name,
      price: item.price_paid
    });
    setSelectedItem(item);
    setModalOpen(true);
  }

  function handleShareOpen() {
    trackShareModalOpened();
    setSharingModalOpen(true);
  }

  function handleLogout() {
    trackLogoutConfirmed();
    logout();
    resetCloset();
    navigate("/onboarding");
  }

  return (
    <div className={styles.app}>
      <Sidebar
        members={MEMBERS}
        categories={[]}
        activeMemberId={activeMemberId}
        activeCategoryName={activeCategoryName}
        onShare={handleShareOpen}
        disabled={!user?.inventory_id}
        onLogout={handleLogout}
      />

      <div ref={containerRef} className={styles.container}>
        {/* Sync banner — only visible while a sync is running */}
        {syncing && (
          <div className={styles.syncHeader}>
            <div className={obStyles.syncBannerRing} />
            <div className={obStyles.syncBannerText}>Syncing your purchases...</div>
            <div className={obStyles.syncBannerBar}>
              <div className={obStyles.syncBannerFill} style={{ width: `${Math.round(syncProgress)}%` }} />
            </div>
            <div className={obStyles.syncBannerPct}>{Math.round(syncProgress)}%</div>
          </div>
        )}

        {/* Header */}
        <div className={styles.header} style={syncing ? { top: 37 } : undefined}>
          <div className={styles.headerTitle}>
            {activeMemberId === "all" ? "All" : MEMBER_NAMES[activeMemberId] || activeMemberId}
          </div>
          <div className={styles.headerLeftActions}>
            <a
              href="https://apps.apple.com/us/app/closai/id6502666606" // <-- replace with your real App Store link
              target="_blank"
              rel="noreferrer"
              aria-label="Open Closai on the App Store"
            >
              <img className={styles.appleBadge} src={AppleBadge} alt="Download on the App Store" />
            </a>
            <Button icon={<Share2 />} onClick={handleShareOpen} fullWidth={false} variant="secondary" />
          </div>
        </div>

        <div className={styles.main} style={syncing ? { paddingTop: 101 } : undefined}>
          {/* Stats */}
          <div className={styles.statsWrap}>
            <div className={styles.statsGrid}>
              <StatCard value={String(user?.products_count || 0)} label="Items" />
              <StatCard value={String(uniqueBrands)} label="Brands" />
              <StatCard value={"$" + avaragePrice} label="Average price per item" />
              <StatCard value={"$" + totalValue} label="Total Value" />
            </div>
          </div>

          {/* Toolbar */}
          <Toolbar />

          {/* Content */}
          <div className={styles.content}>
            {items.length === 0 && !loadingInitialPage ? (
              <div className={styles.empty}>
                <h3>No items found</h3>
                <p>Try adjusting your search or filters.</p>
              </div>
            ) : viewMode === "grid" ? (
              <div className={styles.gridView}>
                {!loadingInitialPage
                  ? items.map((item) => (
                      <GridCard
                        key={item.id}
                        item={item}
                        showMemberBadge={showMemberBadge}
                        onClick={() => handleItemClick(item)}
                      />
                    ))
                  : SAMPLE_ITEMS.map((item) => <EmptyGridCard key={item.id} />)}
              </div>
            ) : (
              <div className={styles.listView}>
                {!loadingInitialPage
                  ? items.map((item) => <ListCard key={item.id} item={item} onClick={() => handleItemClick(item)} />)
                  : SAMPLE_ITEMS.map((item) => <EmptyListCard key={item.id} />)}
              </div>
            )}

            {/* Bottom loader while fetching next page */}
            {loadingNextPage && (
              <div className={styles.loadingMore}>
                <div className={styles.loadingMoreSpinner} />
              </div>
            )}
          </div>
        </div>
      </div>

      <ProductDetailsModal
        data={selectedItem}
        onClose={() => {
          setModalOpen(false);
          setSelectedItem(null);
        }}
        open={modalOpen}
        maxWidth={760}
      />
      <ShareModal visibility={sharingModalOpen} onChange={setSharingModalOpen} />
      <ScrollIndicator visible={scrolled} containerRef={containerRef} />
    </div>
  );
}
